import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getModelVersions, type ModelVersionInfo } from '@/services/analytics.api'
import { usePermissions } from '@/hooks/usePermissions'

const VERSION_STORAGE_KEY = 'snh_selected_version'

interface VersionContextValue {
  versions: ModelVersionInfo[]
  selectedVersion: string | null
  setSelectedVersion: (version: string | null) => void
  currentDeployedVersion: string | null
  isLoading: boolean
  isVersionSelected: boolean
}

const VersionContext = createContext<VersionContextValue | null>(null)

export function VersionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { effectiveTenantId } = usePermissions()
  const tenantKey = effectiveTenantId ?? 'portfolio'

  const [selectedVersion, setSelectedVersionState] = useState<string | null>(
    () => localStorage.getItem(VERSION_STORAGE_KEY),
  )
  const [initialized, setInitialized] = useState(false)
  const knownCurrentRef = useRef<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['model-versions', tenantKey],
    queryFn: () => getModelVersions(),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const versions = data?.versions ?? []

  const currentDeployedVersion = useMemo(() => {
    const current = versions.find(v => v.isCurrent)
    return current?.version ?? versions[0]?.version ?? null
  }, [versions])

  useEffect(() => {
    if (versions.length === 0) return

    if (!initialized) {
      const stored = localStorage.getItem(VERSION_STORAGE_KEY)
      const validStored = stored && versions.some(v => v.version === stored)
      if (validStored) {
        setSelectedVersionState(stored)
      } else if (currentDeployedVersion) {
        setSelectedVersionState(currentDeployedVersion)
        localStorage.setItem(VERSION_STORAGE_KEY, currentDeployedVersion)
      }
      knownCurrentRef.current = currentDeployedVersion
      setInitialized(true)
      return
    }

    // Follow new deploys: if the current deployed version changed and user
    // was still on the old current version, auto-switch.
    const prev = knownCurrentRef.current
    if (prev && currentDeployedVersion && currentDeployedVersion !== prev) {
      if (selectedVersion === prev) {
        setSelectedVersionState(currentDeployedVersion)
        localStorage.setItem(VERSION_STORAGE_KEY, currentDeployedVersion)
      }
      knownCurrentRef.current = currentDeployedVersion
    }
  }, [versions, initialized, currentDeployedVersion, selectedVersion])

  // Reset version when tenant changes
  useEffect(() => {
    if (!initialized) return
    setSelectedVersionState(null)
    localStorage.removeItem(VERSION_STORAGE_KEY)
    setInitialized(false)
  }, [tenantKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const setSelectedVersion = useCallback(
    (version: string | null) => {
      if (version) {
        localStorage.setItem(VERSION_STORAGE_KEY, version)
      } else {
        localStorage.removeItem(VERSION_STORAGE_KEY)
      }
      setSelectedVersionState(version)
      queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const value = useMemo<VersionContextValue>(
    () => ({
      versions,
      selectedVersion,
      setSelectedVersion,
      currentDeployedVersion,
      isLoading,
      isVersionSelected: !!selectedVersion,
    }),
    [versions, selectedVersion, setSelectedVersion, currentDeployedVersion, isLoading],
  )

  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>
}

export function useVersionContext() {
  const ctx = useContext(VersionContext)
  if (!ctx) throw new Error('useVersionContext must be used within a VersionProvider')
  return ctx
}
